<template>
  <div class="image-input el-input" :style="{'--img-span': imgSpan, '--input-span': inputSpan}">
    <div class="image-input-control">
      <div class="image-preview" :style="previewStyle">
        <el-button-group class="btn-shortcut-buttons">
          <el-button icon="el-icon-delete" @click="handleRemove"></el-button>
          <el-button icon="el-icon-download" @click="handleDownloadClick" :disabled="!filePath || downloading"></el-button>
          <el-button title="从文件库中搜索文件" icon="el-icon-search" v-popover:filemanager></el-button>
          <el-button icon="el-icon-more" @click="handleSelectClick"></el-button>
        </el-button-group>
      </div>
      <el-input placeholder="输入图片描述" v-model="fileDesc" type="textarea" :autosize="{minRows: descRows}" v-popover:filepreview></el-input>
    </div>
    <input type="file" accept="image/*" ref="file" @change="handleSelectFile">
    <el-popover
        ref="filepreview"
        placement="bottom-start"
        width="500"
        trigger="click">
      <FilePreview :src="value"></FilePreview>
    </el-popover>
    <el-popover
        ref="filemanager"
        placement="bottom-start"
        width="600"
        trigger="click">
      <FileSelect accept="image/*" :prepage="15" @input="handleSelectInput"></FileSelect>
    </el-popover>
  </div>
</template>

<script>
import {getFileClient} from "@/js/file.js";
import utils from "@/js/utils.js";
import {toFileSize} from "@/js/formatter.js";
import FileSelect from './filemanager/FileSelect';
import FilePreview from './filemanager/FilePreview'

export default {
  name: "ImageInput",
  props: {
    label: String,
    value: String,
    desc: String,
    imgSpan: {
      type: Number,
      default: 1
    },
    inputSpan: {
      type: Number,
      default: 3
    },
    descRows: {
      type: Number,
      default: 2
    }
  },
  components: {FileSelect, FilePreview},
  data() {
    return {
      text: '',
      filePath: '',
      bucket: null,
      uploading: false,
      uploaded: false,
      downloading: false,
      progress: 0,
      nativeValue: null,
      rules: {
        default: {base: '@oss/upload', dest: '/sc/upload', bucket: 'hskonline'},
        cdn: {base: '@cdn', dest: '/', bucket: 'hskonline'},
      },
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal !== null && newVal !== undefined && newVal === this.nativeValue) {
          return
        }
        this.initText()
      },
      immediate: true
    }
  },
  computed: {
    fileDesc: {
      get: function () {
        return this.desc;
      },
      set: function (val) {
        this.$emit('update:desc', val);
      },
    },
    previewStyle() {
      const url = this.value;
      if (!url) return null;
      return {
        backgroundImage: `url(${utils.url(url)})`
      }
    },
  },
  methods: {
    getOss(bucket) {
      return getFileClient(bucket || this.bucket || this.rules.default.bucket);
    },
    initText() {
      this.uploading = false
      this.uploaded = false
      this.$nextTick(() => {
        this.$refs.file.value = ''
      })
      if (this.value) {
        let path = typeof this.value === 'string' ? this.value : this.value[0]
        this.text = path.substring(path.lastIndexOf('/') + 1)
        let rule = this.getRuleByPath(path)
        path = utils.relativeUrl(path, '@base')
        this.filePath = path
        this.bucket = rule ? rule.bucket : null
        this.getOss().filename(path).then(filename => {
          if (filename) {
            this.text = filename;
          }
        })
      } else {
        this.text = ''
      }
    },
    getRuleByPath(path) {
      return Object.values(this.rules).find(r => path.startsWith(r.base));
    },
    showButton(name) {
      return !this.hideTools || this.hideTools.indexOf(name) < 0
    },
    handleCommand(command) {
      if (command in this) this[command]()
    },
    handleDownloadClick() {
      this.downloading = true
      this.getOss().download(this.filePath, this.text).then(res => {
        this.downloading = false
      }).catch(error => {
        this.downloading = false
        console.log(error)
      })
    },
    handleSelectClick() {
      this.$refs.file.click()
    },
    handleSelectInput(url) {
      this.$refs.file.value = ''
      this.$emit('input', url)
      this.$refs.filemanager.doClose()
    },
    handleRemove() {
      this.$refs.file.value = ''
      this.nativeValue = ''
      this.text = ''
      this.filePath = ''
      this.$emit('input', this.nativeValue)
    },
    handleSelectFile(event) {
      if (this.$refs.file.files.length === 0) {
        return
      }
      this.beforeUpload(this.$refs.file.files[0])
      let results = []
      for (let file of this.$refs.file.files) {
        results.push(this.upload(file, (p) => {
          this.progress = p * 100
        }))
      }
      Promise.all(results).then(res => {
        let urls = res.map(f => f.url)
        this.nativeValue = this.multiple ? urls : urls[0]
        this.$emit('input', this.nativeValue)
        this.afterUpload(this.$refs.file.files[0])
      })
    },
    beforeUpload(file) {
      this.uploading = true
      this.uploaded = false
      this.text = '正在上传中...（' + toFileSize(file.size) + '）'
    },
    afterUpload(file) {
      this.uploading = false
      this.uploaded = true
      this.text = file.name + '（' + toFileSize(file.size) + '）'
    },
    getDest(file) {
      if (/^video\//.test(file.type)) {
        return this.rules.video
      }
      if (/^audio\//.test(file.type)) {
        return this.rules.audio
      }
      return this.rules.default
    },
    upload(file, progress) {
      let dest = this.getDest(file);
      return this.getOss(dest.bucket).upload(file, {...dest, progress});
    }
  }
}
</script>

<style lang="scss" scoped>
input[type="file"] {
  display: none;
}
.image-input {
  display: flex;
}
.image-input::v-deep {
  > img {
    max-height: 100%;
  }
}
.image-input-control {
  display: flex;
  flex: 1;
  .btn-shortcut-buttons {
    display: none;
    position: absolute;
    left: 0;
    bottom: 0;
    right: 0;
    > .el-button {
      flex: 1;
      padding: 8px;
      border-bottom: 0;
      &:first-child {
        border-left: 0;
        border-top-left-radius: 0;
      }
      &:last-child {
        border-right: 0;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }
  > .image-preview {
    flex: var(--img-span);
    background-color: #f5f7fa;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center center;
    border: 1px solid #DCDFE6;
    border-radius: 4px 0 0 4px;
    border-right: 0;
    position: relative;
    &:hover .btn-shortcut-buttons {
      display: flex;
    }
  }
  > .el-textarea::v-deep {
    flex: var(--input-span);
    min-height: 40px;
    textarea {
      border-radius: 0 4px 4px 0;
    }
  }
}

</style>